import { getInsuranceTemplateFieldValue } from "./insuranceTemplateValues";
import { CreatableSelect } from "@/components/features/matter/file-repo/GenerateDocument/CreatableSelect";
import {
  getBaseKeyForVariable,
  useAutoSelectSingleOption,
  type InstanceGroup,
  type InsuranceVariablePrefix,
} from "@/components/features/matter/file-repo/GenerateDocument/GenerateDocumentSlideover";
import { type InsuranceWithConnections } from "@/components/features/matter/file-repo/GenerateDocument/templateDictionaryOptions";
import { formatInsuranceTitle } from "@/lib/utils/format";
import {
  useCallback,
  useMemo,
  type Dispatch,
  type SetStateAction,
} from "react";

interface ApplyInsuranceDropdownProps {
  sectionKey: InsuranceVariablePrefix;
  applySuffix: string;
  selectionKey: string;
  selectionValue: string;
  defaultItemId?: string;
  group: InstanceGroup;
  setSectionContactSelections: Dispatch<SetStateAction<Record<string, string>>>;
  setSelectedTemplateDictionaryOptions: Dispatch<
    SetStateAction<Record<string, string>>
  >;
  clientInsurance: InsuranceWithConnections[];
  defendantInsurance: InsuranceWithConnections[];
}

export const ApplyInsuranceDropdown = ({
  sectionKey,
  applySuffix,
  selectionKey,
  selectionValue,
  defaultItemId,
  group,
  setSectionContactSelections,
  setSelectedTemplateDictionaryOptions,
  clientInsurance,
  defendantInsurance,
}: ApplyInsuranceDropdownProps) => {
  const placeholder =
    sectionKey === "clientInsurance"
      ? `Apply Client Insurance${applySuffix}`
      : `Apply Defendant Insurance${applySuffix}`;

  const clientInsuranceSectionOptions = useMemo(
    () =>
      clientInsurance.map((insurance) => ({
        value: insurance.id,
        label:
          formatInsuranceTitle({
            insurance,
          })?.title ?? "",
        context: insurance.policyNumber
          ? `Policy # ${insurance.policyNumber}`
          : "",
      })),
    [clientInsurance],
  );

  const defendantInsuranceSectionOptions = useMemo(
    () =>
      defendantInsurance.map((insurance) => ({
        value: insurance.id,
        label:
          formatInsuranceTitle({
            insurance,
          })?.title ?? "",
        context: insurance.policyNumber
          ? `Policy # ${insurance.policyNumber}`
          : "",
      })),
    [defendantInsurance],
  );

  const options =
    sectionKey === "clientInsurance"
      ? clientInsuranceSectionOptions
      : defendantInsuranceSectionOptions;

  const applySectionInsuranceInstance = useCallback(
    (insuranceId: string) => {
      const insuranceList =
        sectionKey === "clientInsurance" ? clientInsurance : defendantInsurance;
      const insurance = insuranceList.find((i) => i.id === insuranceId);
      if (!insurance) return;

      const updates: Record<string, string> = {};

      group.items.forEach((item) => {
        const base = item.baseVariable || item.variable;
        const targetKey = getBaseKeyForVariable(item.variable);
        const value = getInsuranceTemplateFieldValue(
          insurance,
          base,
          sectionKey,
        );
        if (value !== undefined) {
          updates[targetKey] = value;
        }
      });

      if (Object.keys(updates).length) {
        setSelectedTemplateDictionaryOptions((prev) => ({
          ...prev,
          ...updates,
        }));
      }
    },
    [
      clientInsurance,
      defendantInsurance,
      group,
      sectionKey,
      setSelectedTemplateDictionaryOptions,
    ],
  );

  const handleChange = useCallback(
    (val: string) => {
      setSectionContactSelections((prev) => ({
        ...prev,
        [selectionKey]: val,
      }));
      applySectionInsuranceInstance(val);
    },
    [applySectionInsuranceInstance, selectionKey, setSectionContactSelections],
  );

  useAutoSelectSingleOption(
    defaultItemId
      ? options.filter((option) => option.value === defaultItemId)
      : options,
    selectionValue,
    handleChange,
  );

  return (
    <div className="min-w-[260px]">
      <CreatableSelect
        className="!space-y-0"
        placeholder={placeholder}
        options={options}
        value={selectionValue}
        onChange={handleChange}
        createMessage="Create contact"
      />
    </div>
  );
};
